import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ExternalLink, Loader2, RefreshCw, User, WifiOff } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { phoneToChatId, whatsappService } from "@/services/whatsapp";
import { useWhatsAppChat, ChatSocketStatus } from "./useWhatsAppChat";
import { ChatMessageList } from "./ChatMessageList";
import { ChatComposer } from "./ChatComposer";
import { cn } from "@/lib/utils";

const SOCKET_STATUS: Record<ChatSocketStatus, { label: string; dot: string }> = {
  connected: { label: "En vivo", dot: "bg-emerald-500" },
  connecting: { label: "Conectando...", dot: "bg-amber-500 animate-pulse" },
  disconnected: { label: "Sin conexión en vivo", dot: "bg-muted-foreground" },
};

interface WhatsAppChatSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phone: string;
  name?: string;
}

export function WhatsAppChatSheet({ open, onOpenChange, phone, name }: WhatsAppChatSheetProps) {
  const cleanPhone = phone?.replace(/\D/g, "") ?? "";
  const chatId = useMemo(() => (cleanPhone ? phoneToChatId(cleanPhone) : null), [cleanPhone]);

  const { data: session, isLoading: loadingSession } = useQuery({
    queryKey: ["whatsapp-session"],
    queryFn: async () => {
      try {
        return await whatsappService.getSessionStatus();
      } catch {
        return null;
      }
    },
    retry: false,
    enabled: open,
  });

  const isWorking = session?.status === "WORKING";

  const { data: picture } = useQuery({
    queryKey: ["whatsapp-profile-picture", chatId],
    queryFn: () => whatsappService.getProfilePicture(chatId!).catch(() => null),
    enabled: open && isWorking && !!chatId,
    staleTime: 60 * 60_000,
  });

  const chat = useWhatsAppChat(chatId, open && isWorking);
  const socket = SOCKET_STATUS[chat.socketStatus];

  const initials = (name || cleanPhone)
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const renderBody = () => {
    if (!chatId) {
      return (
        <StateMessage
          icon={<AlertCircle className="h-7 w-7 text-destructive" />}
          title="Sin número de teléfono"
          description="El cliente no tiene un teléfono registrado para abrir el chat."
        />
      );
    }
    if (loadingSession) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      );
    }
    if (!isWorking) {
      return (
        <StateMessage
          icon={<WifiOff className="h-7 w-7 text-amber-500" />}
          title="WhatsApp no está conectado"
          description={
            session
              ? "Conecta tu WhatsApp desde Perfil → Mensajería para chatear con tus clientes."
              : "No hay configuración de WhatsApp activa. Por favor, comuníquese con su proveedor."
          }
        />
      );
    }
    if (chat.error && chat.messages.length === 0) {
      return (
        <StateMessage
          icon={<AlertCircle className="h-7 w-7 text-destructive" />}
          title="No se pudieron cargar los mensajes"
          description={chat.error}
          action={
            <Button variant="outline" size="sm" onClick={chat.refresh} disabled={chat.isRefreshing}>
              <RefreshCw className={cn("mr-2 h-4 w-4", chat.isRefreshing && "animate-spin")} />
              Reintentar
            </Button>
          }
        />
      );
    }
    return (
      <ChatMessageList
        chatId={chatId}
        messages={chat.messages}
        isLoading={chat.isLoading}
        isLoadingEarlier={chat.isLoadingEarlier}
        hasEarlier={chat.hasEarlier}
        onLoadEarlier={chat.loadEarlier}
      />
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
        {/* Header */}
        <div className="flex items-center gap-3 border-b px-4 py-3 pr-12">
          <Avatar className="h-10 w-10">
            {picture && <AvatarImage src={picture} alt={name} />}
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
              {initials || <User className="h-4 w-4" />}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <SheetTitle className="truncate text-base">{name || cleanPhone}</SheetTitle>
            <SheetDescription className="flex items-center gap-2 text-xs">
              <span>+{cleanPhone}</span>
              {isWorking && (
                <span className="flex items-center gap-1">
                  <span className={cn("h-1.5 w-1.5 rounded-full", socket.dot)} />
                  {socket.label}
                </span>
              )}
            </SheetDescription>
          </div>
          {isWorking && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={chat.refresh}
                  disabled={chat.isRefreshing || chat.isLoading}
                >
                  <RefreshCw className={cn("h-4 w-4", chat.isRefreshing && "animate-spin")} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Actualizar mensajes</TooltipContent>
            </Tooltip>
          )}
          {cleanPhone && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                  <a href={`https://wa.me/${cleanPhone}`} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Abrir en WhatsApp</TooltipContent>
            </Tooltip>
          )}
        </div>

        {renderBody()}

        <ChatComposer
          disabled={!isWorking || !chatId || chat.isLoading}
          onSendText={chat.sendText}
          onSendMedia={chat.sendMedia}
        />
      </SheetContent>
    </Sheet>
  );
}

function StateMessage({
  icon,
  title,
  description,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center bg-muted/30">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background shadow-sm">{icon}</div>
      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground max-w-xs">{description}</p>
      </div>
      {action}
    </div>
  );
}
